// Notes in any format (module 10 U209; pipeline I2, REQ-2026-10-04-31): the API shapes, a module of this track's own as
// deals/api.ts is the Deals page's (the portal's api.ts is another track's file, infra/agent-guard/OWNERS).
import { viewFetch as fetch } from '../view-as.js';
import { Unauthenticated } from '../api.js';

export type NoteKind = 'photo' | 'pdf' | 'document' | 'sheet' | 'text' | 'mail' | 'audio';
export type NoteState = 'waiting' | 'reading' | 'read' | 'duplicate' | 'refused' | 'failed';

export interface Note {
  id: string;
  name: string;
  kind: NoteKind;
  state: NoteState;
  reason: string | null;
  uploadedAt: string;
  readAt: string | null;
  pages: number | null;
  /** Words the reader was not sure of, for the writer to check against the page. */
  unsure: { text: string; page: number }[];
  signalId: string | null;
  signalCount: number;
  text: string | null;
  subject: string | null;
  hasPage: boolean;
  /** The meeting or company the note was added to, by name. */
  meeting: string | null;
}

/** What one upload came to: read now, waiting for the reader, the same file again, or refused with why. */
export type Uploaded =
  | { ok: true; id: string; state: 'read' | 'waiting'; kind: NoteKind }
  | { ok: true; id: string; state: 'duplicate' | 'refused'; reason: string; kind: NoteKind }
  | { ok: false; error: string };

/** The file types a note may be, for the picker. The server decides; this only narrows what the picker offers. */
export const NOTE_ACCEPT = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.docx', '.pptx', '.rtf', '.xlsx', '.csv', '.txt', '.md', '.eml',
  '.m4a', '.mp3', '.wav', '.webm', '.ogg', '.opus', '.aac', '.mp4',
].join(',');

const q = (store: string, extra: Record<string, string | null | undefined> = {}) => {
  const p = new URLSearchParams({ store });
  for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
  return p.toString();
};

export const notesApi = {
  list: (store: string) =>
    fetch(`/api/notes?${q(store)}`, { credentials: 'same-origin' }).then(async (r) => {
      if (r.status === 401) throw new Unauthenticated();
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      // BUGS.md D113: the newest 50, and how many are yours in all (an older server sends no total: the list is all).
      const b = (await r.json()) as { notes: Note[]; total?: number };
      return { notes: b.notes, total: b.total ?? b.notes.length };
    }),
  /** The file itself as the body; its name and its own type travel in headers, so any file goes up the same way. */
  upload: (store: string, file: File, on: { meeting?: string | null; company?: string | null; deal?: string | null } = {}): Promise<Uploaded> =>
    fetch(`/api/notes?${q(store, { meeting: on.meeting, company: on.company, deal: on.deal })}`, {
      method: 'POST', credentials: 'same-origin', body: file,
      headers: { 'content-type': 'application/octet-stream', 'x-note-name': encodeURIComponent(file.name), ...(file.type ? { 'x-note-type': file.type } : {}) },
    }).then(async (r) => {
      if (r.status === 401) throw new Unauthenticated();
      const j = (await r.json().catch(() => ({}))) as { error?: string; id?: string; state?: 'read' | 'waiting' | 'duplicate' | 'refused'; reason?: string; kind?: NoteKind };
      if (!r.ok || !j.id || !j.state || !j.kind) return { ok: false, error: j.error ?? (r.status === 413 ? 'The file is larger than 25 MB. Nothing was uploaded.' : 'It was not uploaded.') };
      if (j.state === 'duplicate' || j.state === 'refused') return { ok: true, id: j.id, state: j.state, reason: j.reason ?? '', kind: j.kind };
      return { ok: true, id: j.id, state: j.state, kind: j.kind };
    }),
  pageUrl: (store: string, id: string) => `/api/notes/${encodeURIComponent(id)}/page?${q(store)}`,
};
